import React from 'react';
import { Info, AlertTriangle, Wrench, AlertCircle } from 'lucide-react';
import { useHealth, MaintenanceSeverity } from '../hooks/queries/useHealth';
import { useAuth } from '../contexts/AuthContext';
import { isAdminUser } from '../utils/tier-utils';
import { Banner, type BannerTone } from './ui';

type SeverityConfig = {
  tone: BannerTone;
  icon: React.ReactNode;
  title: string;
  text: string;
};

const SEVERITY_CONFIG: Record<MaintenanceSeverity, SeverityConfig> = {
  INFO: {
    tone: 'info',
    icon: <Info className="h-5 w-5" aria-hidden="true" />,
    title: 'Systemmeldung',
    text: 'Es gibt eine neue Systemmeldung.',
  },
  WARNING: {
    tone: 'warning',
    icon: <AlertTriangle className="h-5 w-5" aria-hidden="true" />,
    title: 'Warnung',
    text: 'Bitte beachte mögliche Einschränkungen.',
  },
  MAINTENANCE: {
    tone: 'warning',
    icon: <Wrench className="h-5 w-5" aria-hidden="true" />,
    title: 'Wartungsarbeiten',
    text: 'Wir optimieren derzeit unsere Suche. Das Anlegen, Bearbeiten oder Löschen von Suchen ist momentan nicht möglich. Bitte versuche es in Kürze erneut.',
  },
  OUTAGE: {
    tone: 'danger',
    icon: <AlertCircle className="h-5 w-5" aria-hidden="true" />,
    title: 'Systemausfall',
    text: 'Das System ist derzeit nicht verfügbar. Wir arbeiten daran, das Problem zu beheben.',
  },
};

const MaintenanceBanner: React.FC = () => {
  const { data: health } = useHealth();
  const { user } = useAuth();

  const isAdmin = isAdminUser(user);

  if (!health?.maintenanceMode) return null;

  const severity: MaintenanceSeverity = health.maintenanceSeverity ?? 'MAINTENANCE';
  const cfg = SEVERITY_CONFIG[severity];
  const displayText = health.maintenanceText?.trim()
    ? health.maintenanceText
    : (isAdmin ? `[Admin] ${cfg.text}` : cfg.text);

  return (
    <div className="sticky top-0 z-[9999]">
      <Banner tone={cfg.tone} title={cfg.title} icon={cfg.icon} className="rounded-none">
        {displayText}
      </Banner>
    </div>
  );
};

export default MaintenanceBanner;
